import React from "react";

interface QuotaData {
  maxJobsPerDay: number;
  maxPagesPerJob: number;
  maxPagesPerMonth: number;
  jobsToday: number;
  pagesThisMonth: number;
}

interface ScraperUsageBannerProps {
  quota: QuotaData | null;
}

function UsageBar({ used, max, label }: { used: number; max: number; label: string }) {
  const pct = max > 0 ? Math.min(100, (used / max) * 100) : 0;
  const color = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-blue-500";

  return (
    <div className="flex-1 min-w-0">
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>{label}</span>
        <span className={pct >= 90 ? "text-red-600 font-medium" : ""}>
          {used.toLocaleString()} / {max.toLocaleString()}
        </span>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-1.5">
        <div className={`${color} h-1.5 rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function ScraperUsageBanner({ quota }: ScraperUsageBannerProps) {
  if (!quota) return null;

  const jobPct = quota.maxJobsPerDay > 0 ? (quota.jobsToday / quota.maxJobsPerDay) * 100 : 0;
  const pagePct = quota.maxPagesPerMonth > 0 ? (quota.pagesThisMonth / quota.maxPagesPerMonth) * 100 : 0;
  const isNearLimit = jobPct >= 80 || pagePct >= 80;

  return (
    <div className={`rounded-lg border px-4 py-3 mb-4 ${isNearLimit ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
      <div className="flex flex-wrap gap-6 items-center">
        <UsageBar
          used={quota.jobsToday}
          max={quota.maxJobsPerDay}
          label="Jobs today"
        />
        <UsageBar
          used={quota.pagesThisMonth}
          max={quota.maxPagesPerMonth}
          label="Pages this month"
        />
        <div className="text-xs text-slate-400 flex-shrink-0">
          Max {quota.maxPagesPerJob.toLocaleString()} pages/job
        </div>
      </div>
      {isNearLimit && (
        <p className="text-xs text-amber-700 mt-2">
          You're approaching your scraper quota. Contact support to increase your limits.
        </p>
      )}
    </div>
  );
}
